import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { CUSTOM_PDF_REPORTS_LIST_STATE_KEYS } from 'CustomPDFReportsApp/ts/apps/custom-pdf-reports-list-app/state/constants';
import { CustomPDFReportOrientation } from '../enums/custom-pdf-report-orientation.enum';
import { CustomPDFReportType } from 'CustomPDFReportsApp/ts/enums/custom-pdf-report-type.enum';
import { FolderableType } from 'FoldersApp/enums/folderable-type.enum';
import { CustomPDFReportModuleMenu } from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/types/custom-pdf-report-module-menu.type';
import { SecurityGroupName } from 'SecurityListsApp/ts/features/security-list-selector-modals/components/security-list-group-tabs/types/security-group-name.type';
import { LooseObject } from 'Angular/interfaces/loose-object.interface';
import {
    CustomPDFReportModuleCustomizableFieldValues,
    CustomPDFReportModuleSaveState,
} from 'CustomPDFReportsApp/ts/apps/custom-pdf-report-builder-app/interfaces/custom-pdf-report-module.interface';
import { CustomPDFReportsListFoldersStore } from 'CustomPDFReportsApp/ts/apps/custom-pdf-reports-list-app/state/interfaces/custom-pdf-reports-list-store.interface';
import {
    CustomPDFReportCreateParams,
    CustomPDFReportDuplicateViewResponse,
    CustomPDFReportGenerateServiceResponse,
    CustomPDFReportGenerationParams,
    CustomPDFReportGetResponse,
    CustomPDFReportParams,
    CustomPDFReportPostResponse,
    CustomPDFReportPutResponse,
    CustomPDFReportReorientParams,
    CustomPDFReportReorientResponse,
    CustomPDFReportsCollectionGetViewResponse,
    CustomPDFReportsCollectionResponseGetParams,
    CustomPDFReportSectionPreferenceGetResponse,
    CustomPDFReportSectionPreferencePutParams,
    CustomPDFReportSectionPreferencePutResponse,
    CustomPDFReportsFolderableCollectionViewGetResponse,
    CustomPDFReportTemplateGetData,
    CustomPFDReportSaveParams,
    GenerateReportCollectionGetParams,
    GenerateReportCollectionGetResponse,
    ReportBrandingShareGroupsResponse,
    ValidateCustomPDFReportModuleParams,
    ValidateReportSecurityListResponse,
} from 'CustomPDFReportsApp/ts/interfaces/custom-pdf-report-views.interface';
import {
    CustomPDFReport,
    CustomPDFReportDataSource,
    CustomPDFReportMenuByType,
    WizardStep,
} from 'CustomPDFReportsApp/ts/interfaces/custom-pdf-reports.interfaces';

import { AccountsService } from 'AccountsApp/ts/services/accounts.service';
import { AjaxService } from 'Angular/services/core/ajax.service';
import { UrlsService } from 'Angular/services/core/urls.service';

import { getListPageRedirectUrl } from 'AccountsApp/ts/utils/utils';
import { formFlattenedObject } from 'Angular/utils/urls';

@Injectable({
    providedIn: 'root',
})
export class CustomPDFReportService {
    constructor(
        private accountsService: AccountsService,
        private ajaxService: AjaxService,
        private urlsService: UrlsService
    ) {}

    async getCustomPDFReportsListPageRedirectUrl(
        overrides?: Partial<CustomPDFReportsListFoldersStore>
    ): Promise<string> {
        return getListPageRedirectUrl(
            this.accountsService,
            this.urlsService.CUSTOM_PDF_REPORTS.LIST_PAGE,
            'custom_pdf_reports_list_page',
            CUSTOM_PDF_REPORTS_LIST_STATE_KEYS,
            overrides
        );
    }

    getGenerateReports(params: GenerateReportCollectionGetParams): Observable<GenerateReportCollectionGetResponse> {
        const url = this.urlsService.CUSTOM_PDF_REPORTS.GENERATE_COLLECTION;
        const queryParams = formFlattenedObject(params, [
            'dataSource',
            'name',
            'owner',
            'reportType',
            'language',
            'baseResourceType',
            'sortColumn',
            'sortDirection',
        ]);

        // Add optional parameters only if they exist
        if (params.securityId) {
            queryParams.securityId = params.securityId;
        }

        if (params.securitylistName) {
            queryParams.securitylistName = params.securitylistName;
        }
        return this.ajaxService.getDataObserveBody<GenerateReportCollectionGetResponse>(url, queryParams);
    }

    getGenerateReportInfo(reportId: number, baseResourceType: FolderableType | null): Observable<CustomPDFReport> {
        const url = this.urlsService.getUrl(this.urlsService.CUSTOM_PDF_REPORTS.GENERATE_REPORT_MODAL, { reportId });
        const params = baseResourceType ? { baseResourceType } : {};
        return this.ajaxService.getDataObserveBody<CustomPDFReport>(url, params);
    }

    getReportBrandingShareGroups(): Observable<ReportBrandingShareGroupsResponse> {
        const url = this.urlsService.ACCOUNTS.SHARE_GROUPS_REPORT_BRANDING;
        return this.ajaxService.getDataObserveBody<ReportBrandingShareGroupsResponse>(url);
    }

    getTearsheetPDFReports(
        reportType: CustomPDFReportType,
        reportOrientation: CustomPDFReportOrientation | null
    ): Observable<CustomPDFReportMenuByType[]> {
        const url = this.urlsService.CUSTOM_PDF_REPORTS.TEARSHEET_COLLECTION;
        return this.ajaxService.getDataObserveBody<CustomPDFReportMenuByType[]>(url, { reportType, reportOrientation });
    }

    getPDFReportModuleCategoryMenu(
        reportOrientation: CustomPDFReportOrientation,
        reportType: CustomPDFReportType,
        dataSource: CustomPDFReportDataSource,
        numRequiredSecurities: number | null = null
    ): Observable<CustomPDFReportModuleMenu> {
        const queryParams: Record<string, string> = {
            orientation: reportOrientation,
            dataSource,
        };
        if (numRequiredSecurities !== null) {
            queryParams.numRequiredSecurities = String(numRequiredSecurities);
        }
        const url = this.urlsService.getUrl(
            this.urlsService.CUSTOM_PDF_REPORTS.MODULE_MENU,
            { reportType },
            queryParams
        );
        return this.ajaxService.getDataObserveBody<CustomPDFReportModuleMenu>(url);
    }

    getPDFReport(
        reportId: number,
        reportOrientation: CustomPDFReportOrientation | null = null
    ): Observable<CustomPDFReportGetResponse> {
        let queryParams = {};
        if (reportOrientation) {
            queryParams = { reportOrientation };
        }
        const url = this.urlsService.getUrl(this.urlsService.CUSTOM_PDF_REPORTS.PDF_REPORT, { reportId }, queryParams);
        return this.ajaxService.getDataObserveBody<CustomPDFReportGetResponse>(url);
    }

    postPDFReport(postParams: CustomPDFReportCreateParams): Observable<CustomPDFReportPostResponse> {
        const url = this.urlsService.CUSTOM_PDF_REPORTS.COLLECTION;
        const cleanedPostParams = this.cleanPostParams(postParams);
        return this.ajaxService.postJsonDataObserveBody<CustomPDFReportPostResponse>(url, cleanedPostParams);
    }

    putPDFReport(reportId: number, putParams: CustomPFDReportSaveParams): Observable<CustomPDFReportPutResponse> {
        const url = this.urlsService.getUrl(this.urlsService.CUSTOM_PDF_REPORTS.PDF_REPORT, { reportId });
        return this.ajaxService.putJsonDataObserveBody<CustomPDFReportPutResponse>(url, putParams);
    }

    generatePDFReport(
        reportId: number,
        postParams: CustomPDFReportGenerationParams
    ): Observable<CustomPDFReportGenerateServiceResponse> {
        const url = this.urlsService.getUrl(this.urlsService.CUSTOM_PDF_REPORTS.PDF_REPORT, { reportId });
        return this.ajaxService.postJsonDataObserveBody<CustomPDFReportGenerateServiceResponse>(url, postParams);
    }

    generateUnsavedPDFReport(
        generationParams: CustomPDFReportGenerationParams,
        reportParams: CustomPDFReportParams,
        modules: CustomPDFReportModuleSaveState[][],
        filterLegacy = false
    ): Observable<CustomPDFReportGenerateServiceResponse> {
        const queryParams = filterLegacy ? { filterLegacy } : {};
        const url = this.urlsService.getUrl(
            this.urlsService.CUSTOM_PDF_REPORTS.UNSAVED_REPORT_GENERATION,
            {},
            queryParams
        );
        return this.ajaxService.postJsonDataObserveBody<CustomPDFReportGenerateServiceResponse>(url, {
            generationParams,
            reportParams,
            modules,
        });
    }

    deletePDFReport(reportId: number): Observable<HttpResponse<{}>> {
        const url = this.urlsService.getUrl(this.urlsService.CUSTOM_PDF_REPORTS.PDF_REPORT, { reportId });
        return this.ajaxService.deleteDataObserveResponse<{}>(url);
    }

    getSectionPreference(reportId: number): Observable<CustomPDFReportSectionPreferenceGetResponse> {
        const url = this.urlsService.getUrl(this.urlsService.CUSTOM_PDF_REPORTS.SECTION_PREFERENCES, { reportId });
        return this.ajaxService.getDataObserveBody<CustomPDFReportSectionPreferenceGetResponse>(url);
    }

    putSectionPreference(
        reportId: number,
        putParams: CustomPDFReportSectionPreferencePutParams
    ): Observable<CustomPDFReportSectionPreferencePutResponse> {
        const url = this.urlsService.getUrl(this.urlsService.CUSTOM_PDF_REPORTS.SECTION_PREFERENCES, { reportId });
        return this.ajaxService.putJsonDataObserveBody<CustomPDFReportSectionPreferencePutResponse>(url, putParams);
    }

    deleteSectionPreference(reportId: number): Observable<HttpResponse<{}>> {
        const url = this.urlsService.getUrl(this.urlsService.CUSTOM_PDF_REPORTS.SECTION_PREFERENCES, { reportId });
        return this.ajaxService.deleteDataObserveResponse<{}>(url);
    }

    duplicatePDFReport(
        reportId: number,
        newName: string,
        targetFolder?: number | null
    ): Observable<CustomPDFReportDuplicateViewResponse> {
        const urlParams = { reportId };
        const postParams = { name: newName, targetFolder };
        const url = this.urlsService.getUrl(this.urlsService.CUSTOM_PDF_REPORTS.DUPLICATE, urlParams);
        return this.ajaxService.postJsonDataObserveBody<CustomPDFReportDuplicateViewResponse>(url, postParams);
    }

    getPDFReportsList(
        getParams: CustomPDFReportsCollectionResponseGetParams
    ): Observable<CustomPDFReportsCollectionGetViewResponse | CustomPDFReportsFolderableCollectionViewGetResponse> {
        const url = this.urlsService.CUSTOM_PDF_REPORTS.LIST;
        return this.ajaxService.getDataObserveBody<CustomPDFReportsCollectionGetViewResponse>(url, getParams);
    }

    getFolderableItems(
        params: CustomPDFReportsCollectionResponseGetParams
    ): Observable<CustomPDFReportsFolderableCollectionViewGetResponse> {
        return this.ajaxService.getDataObserveBody(this.urlsService.CUSTOM_PDF_REPORTS.LIST_MODAL, params);
    }

    getPDFReportTemplates(): Observable<CustomPDFReportTemplateGetData[]> {
        const url = this.urlsService.CUSTOM_PDF_REPORTS.TEMPLATES;
        return this.ajaxService.getDataObserveBody<CustomPDFReportTemplateGetData[]>(url);
    }

    validatePDFReportModule(
        reportType: CustomPDFReportType,
        module: ValidateCustomPDFReportModuleParams
    ): Observable<{ error_message?: string; params?: { [key: string]: string } }> {
        const url = this.urlsService.getUrl(this.urlsService.CUSTOM_PDF_REPORTS.VALIDATE_MODULE, { reportType });
        return this.ajaxService.postJsonDataObserveBody<{ error_message?: string; params?: { [key: string]: string } }>(
            url,
            module
        );
    }

    validateReportSecurityList(
        reportId: number,
        securityListName: string,
        securityGroup: SecurityGroupName,
        isOnePageReport = false
    ): Observable<ValidateReportSecurityListResponse> {
        const url = this.urlsService.getUrl(this.urlsService.CUSTOM_PDF_REPORTS.VALIDATE_SECURITY_LIST, {
            reportId,
            securityListName,
            securityGroup,
            isOnePageReport,
        });
        return this.ajaxService.getDataObserveBody<ValidateReportSecurityListResponse>(url);
    }

    validateReportSecurities(
        reportId: number,
        modules: CustomPDFReportModuleSaveState[][] | null,
        security_ids: string[],
        securitylist_group: string | undefined
    ): Observable<void> {
        const url = this.urlsService.getUrl(this.urlsService.CUSTOM_PDF_REPORTS.VALIDATE_SECURITIES, {
            reportId,
        });
        return this.ajaxService.postJsonDataObserveBody<void>(url, { security_ids, securitylist_group, modules });
    }

    reorientPDFReport(postParams: CustomPDFReportReorientParams): Observable<CustomPDFReportReorientResponse> {
        const url = this.urlsService.CUSTOM_PDF_REPORTS.REORIENT_PDF;
        return this.ajaxService.postJsonDataObserveBody<CustomPDFReportReorientResponse>(url, postParams);
    }

    /**
     * @param excludedModuleIndexes Indexes into `modules` of the modules left out of the report's
     *   sections. They raise no wizard steps of their own, but still count towards the steps that
     *   run ahead of the Sections step.
     */
    getWizardStepsFromModules(
        modules: CustomPDFReportModuleSaveState[],
        reportType: CustomPDFReportType,
        reportOrientation: CustomPDFReportOrientation,
        includesSections: boolean,
        dataSource: CustomPDFReportDataSource | undefined,
        excludedModuleIndexes: number[] = []
    ): Observable<WizardStep[]> {
        const url = this.urlsService.CUSTOM_PDF_REPORTS.WIZARD_STEPS;
        return this.ajaxService.postJsonDataObserveBody<WizardStep[]>(url, {
            reportType,
            reportOrientation,
            modules,
            includesSections,
            dataSource,
            excludedModuleIndexes,
        });
    }

    /**
     * A saved report's wizard steps for the sections the user kept. The server owns the
     * section-to-module mapping here, so unlike `getWizardStepsFromModules` there's no module list
     * to send.
     */
    getSavedReportWizardSteps(reportId: number, hiddenSectionIds: number[]): Observable<WizardStep[]> {
        const url = this.urlsService.getUrl(this.urlsService.CUSTOM_PDF_REPORTS.SAVED_REPORT_WIZARD_STEPS, {
            reportId,
        });
        return this.ajaxService
            .postJsonDataObserveBody<{ wizard_steps: WizardStep[] }>(url, { hidden_section_ids: hiddenSectionIds })
            .pipe(map((response) => response.wizard_steps));
    }

    validateDataSource(
        reportParams: CustomPDFReportParams,
        modules: CustomPDFReportModuleSaveState[][]
    ): Observable<any> {
        // We do not care about the success responses, only the error responses
        const url = this.urlsService.getUrl(this.urlsService.CUSTOM_PDF_REPORTS.VALIDATE_DATA_SOURCE);
        return this.ajaxService.postJsonDataObserveBody<CustomPDFReportGenerateServiceResponse>(url, {
            reportParams,
            modules,
        });
    }

    private cleanPostParams(postParams: CustomPDFReportReorientParams): CustomPDFReportReorientParams {
        // Remove module IDs from save params since we want to create new modules on the new report
        // Remove talking point IDs from save params since they are detected as invalid in the backend
        return {
            ...postParams,
            modules: postParams.modules.map((page) => {
                return page.map((module) => {
                    const { customizable_fields } = module;
                    const talking_points = customizable_fields.talking_points as LooseObject[] | undefined;
                    const sanitized_fields: CustomPDFReportModuleCustomizableFieldValues = talking_points
                        ? {
                              ...customizable_fields,
                              talking_points: talking_points.map((point) => ({ ...point, id: null })),
                          }
                        : customizable_fields;
                    return {
                        ...module,
                        module_id: null,
                        customizable_fields: sanitized_fields,
                    };
                });
            }),
        };
    }
}
